/**
 * Staff "view as" (read-only) — the client side of `accounts.view_as` (CLAUDE.md §3).
 *
 * A Build staff member opens `/view-as#code=…` from the back office. The page swaps
 * the local database for an in-memory one (nothing of the person's reaches this
 * browser's IndexedDB), exchanges the one-time code for a read-only access token
 * (memory only: no refresh token, no `kb_rt`, never the staff member's own
 * session) and runs the app as that person until the session ends.
 *
 * While it runs:
 *  - every mutating request is refused here before it leaves (`VIEW_AS_READ_ONLY`,
 *    the server refuses it too) and the shell shows a read-only notice;
 *  - nothing is queued offline: the outbox and the media queue refuse writes;
 *  - a 401 never refreshes (that would use the staff member's own cookie): the
 *    session simply ends;
 *  - the account's language is never adopted on this device;
 *  - a persistent banner says whose account it is, that it is read-only, when it
 *    ends, and offers Exit (back to the back office).
 *
 * This module holds state only (no API imports) so the API client can read it.
 */
import { useSyncExternalStore } from 'react';

import { ApiError } from '@/lib/api/errors';

export const VIEW_AS_READ_ONLY = 'VIEW_AS_READ_ONLY';
/** The one write a view-as token may make: ending its own session. */
export const VIEW_AS_END_PATH = '/auth/view-as/end';

export type ViewAsInfo = {
  session_id: string;
  ends_at: string;
  user: { id: string; display_name: string };
  staff_first_name: string;
  read_only: boolean;
  return_url: string;
};

export type ViewAsEnded = 'exited' | 'expired' | 'ended';

export type ViewAsSnapshot = {
  active: ViewAsInfo | null;
  /** Why the last view-as session in this tab ended (the "ended" screen says so). */
  ended: ViewAsEnded | null;
  /** Where the staff member goes back to (their back-office page of the person). */
  returnUrl: string | null;
};

let snapshot: ViewAsSnapshot = { active: null, ended: null, returnUrl: null };
const listeners = new Set<() => void>();
const readOnlyListeners = new Set<() => void>();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function set(next: ViewAsSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getViewAs(): ViewAsSnapshot {
  return snapshot;
}

export function isViewAs(): boolean {
  return snapshot.active !== null;
}

export function subscribeToViewAs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useViewAs(): ViewAsSnapshot {
  return useSyncExternalStore(subscribeToViewAs, getViewAs, getViewAs);
}

/** Start: held in memory only, and ended locally at `ends_at` even with no request. */
export function beginViewAs(info: ViewAsInfo): void {
  if (expiryTimer) clearTimeout(expiryTimer);
  const left = new Date(info.ends_at).getTime() - Date.now();
  expiryTimer = setTimeout(() => endViewAsLocally('expired'), Math.max(0, left));
  set({ active: info, ended: null, returnUrl: info.return_url });
}

/** The session is over (exit, time up, refused by the server): nothing stays behind. */
export function endViewAsLocally(reason: ViewAsEnded): void {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  if (!snapshot.active) return;
  set({ active: null, ended: reason, returnUrl: snapshot.returnUrl });
}

/** Someone tried to change something: the shell shows the read-only notice. */
export function subscribeToReadOnlyAttempts(listener: () => void): () => void {
  readOnlyListeners.add(listener);
  return () => readOnlyListeners.delete(listener);
}

export function readOnlyError(): ApiError {
  return new ApiError({
    code: VIEW_AS_READ_ONLY,
    message: "This is a read-only view of someone else's account: nothing can be changed from it.",
    status: 403,
    retryable: false,
  });
}

/** Tell the shell a change was refused (the server's VIEW_AS_READ_ONLY lands here too). */
export function noteReadOnlyAttempt(): void {
  readOnlyListeners.forEach((listener) => listener());
}

/** Refuse a local write (outbox, media queue, any mutating request) while viewing as someone. */
export function assertWritable(): void {
  if (!isViewAs()) return;
  noteReadOnlyAttempt();
  throw readOnlyError();
}

/** Test helper. */
export function resetViewAsForTests(): void {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  set({ active: null, ended: null, returnUrl: null });
}
